import { Host, Component, State, Prop, h, Element, Event, EventEmitter, forceUpdate } from '@stencil/core';
import { open_in_new } from 'pn-design-assets/pn-assets/icons.js';
import { ripple } from '@/globals/helpers';

/**
 * The `pn-tile` is a navigational element that is intended to be accompanied by an illustration.
 *
 * It is very important that you use the `label` and `url`. This component renders an `a` element if the URL prop is set,
 * so you do not need to wrap or nest the tile in another `a` element. Doing so makes the component inaccessible.
 *
 * @slot illustration - Set a custom SVG. We recommend you use the `pn-illustration` component.
 * This is the same as the `illustration` prop.
 */
@Component({
  tag: 'pn-tile',
  styleUrl: 'pn-tile.scss',
})
export class PnTile {
  private mo: MutationObserver;

  private tile: HTMLDivElement;
  private illustrationSlot: HTMLDivElement;

  @State() visibleOutline: boolean = false;

  @Element() hostElement: HTMLElement;

  /** Label of the link. This is required for the component to be accessible. */
  @Prop() label!: string;
  /** Set a string of text for the tile. Same as the default slot. */
  @Prop() text?: string;

  /** The link of the tile. This is required for the component to be accessible. @category href */
  @Prop() url!: string;
  /** The target attribute of the link. @category href */
  @Prop() target?: string;
  /** The rel attribute of the link. @category href */
  @Prop() rel?: string = 'noopener noreferrer';

  /** The SVG content of an illustration. @category Image */
  @Prop() illustration?: string;
  /** The SVG content of a decorative icon. @category Image */
  @Prop() icon?: string;

  /** Use the horizontal tile. @category Layout */
  @Prop() horizontal?: boolean = false;

  /**
   * Emitted when the link is clicked. Use the `click` property to `preventDefault`
   * if you wish to perform the navigation programmatically.
   **/
  @Event() pnTile: EventEmitter<{ click: MouseEvent }>;

  connectedCallback() {
    this.mo = new MutationObserver(() => forceUpdate(this.hostElement));
    this.mo.observe(this.hostElement, { subtree: true, childList: true });
  }

  disconnectedCallback() {
    this.mo?.disconnect();
  }

  private isExternal() {
    return this.target === '_blank';
  }

  private showIcon() {
    return !!this.icon && !this.showSlotIllustration();
  }

  private showPropIllustration() {
    return !!this.illustration && !this.showSlotIllustration();
  }

  private showSlotIllustration() {
    return !!this.illustrationSlot?.querySelector('[slot="illustration"]');
  }

  private showImage() {
    return this.showIcon() || this.showPropIllustration() || this.showSlotIllustration();
  }

  private handleFocus({ type, target }: FocusEvent) {
    this.visibleOutline = type === 'focus' && (target as HTMLLinkElement).matches(':focus-visible');
  }

  private clickHandler(event: MouseEvent): void {
    ripple(event, this.tile);
    this.pnTile.emit({ click: event });
  }

  render() {
    return (
      <Host>
        <div
          class="pn-tile"
          data-horizontal={this.horizontal}
          data-href={!!this.url}
          data-focus={this.visibleOutline}
          ref={el => (this.tile = el)}
        >
          <div
            class="pn-tile-illustration"
            data-circle={!!this.url}
            data-icon={this.showIcon()}
            role="presentation"
            hidden={!this.showImage()}
          >
            {this.showPropIllustration() ? (
              <pn-illustration illustration={this.illustration} />
            ) : (
              this.showIcon() && <pn-icon color="blue700" icon={this.icon} />
            )}

            <div
              class="pn-tile-illustration-slot"
              hidden={!this.showSlotIllustration()}
              ref={el => (this.illustrationSlot = el)}
            >
              <slot name="illustration" />
            </div>
          </div>

          <div class="pn-tile-text">
            <h3 class="pn-tile-title">
              {this.url ? (
                <a
                  class="pn-tile-link"
                  href={this.url}
                  target={this.target}
                  rel={!this.rel && this.isExternal() ? 'noopener noreferrer' : this.rel}
                  onClick={e => this.clickHandler(e)}
                  onFocus={event => this.handleFocus(event)}
                  onBlur={event => this.handleFocus(event)}
                >
                  {this.label}
                </a>
              ) : (
                this.label
              )}
              {this.isExternal() && <pn-icon icon={open_in_new} small={true} color="blue700" />}
            </h3>
            <div class="pn-tile-slot">
              {!!this.text && <p class="pn-tile-paragraph">{this.text}</p>}
              <slot />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
